import React from 'react';
import { Row, Col } from 'antd';
import * as Yup from 'yup';

import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import Input from 'components/formik/Input';
import { BOUsersBankingInfoTypeEnum } from 'api/backofficeApiClient';
import { maxTextInputCharactersLength } from 'utils/validationUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

export const isInternationalTypeSelected = (
  bankingInfoType: BOUsersBankingInfoTypeEnum,
) => bankingInfoType === BOUsersBankingInfoTypeEnum.International;

export interface InternationalFormValues {
  accountHolderName: string;
  internationalBankName: string;
  internationalAccountNumber: string;
  swiftCode: string;
  internationalRoutingCode: string;
  internationalConfirmation1: boolean;
  internationalConfirmation2: boolean;
}

export const internationalValidationSchema = Yup.object({
  accountHolderName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Account holder name should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  internationalBankName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Bank name should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  internationalAccountNumber: Yup.string()
    .trim()
    .required('Field is required')
    .max(250, 'Account number should be 250 characters maximum'),
  swiftCode: Yup.string()
    .trim()
    .required('Field is required')
    .max(250, 'Bank swift code should be 250 characters maximum'),
  internationalRoutingCode: Yup.string()
    .trim()
    .required('Field is required')
    .max(250, 'International routing code should be 250 characters maximum'),
  internationalConfirmation1: Yup.boolean().oneOf(
    [true],
    'Confirmation is required',
  ),
  internationalConfirmation2: Yup.boolean().oneOf(
    [true],
    'Confirmation is required',
  ),
});

export default function International() {
  const isAdmin = useAppSelector(isAdminSelector);

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem
            name="internationalDetail.accountHolderName"
            label="Account holder name *"
          >
            <Input
              name="internationalDetail.accountHolderName"
              placeholder="Enter account holder name"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="internationalDetail.internationalBankName"
            label="Bank name *"
          >
            <Input
              name="internationalDetail.internationalBankName"
              placeholder="Enter bank name"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="internationalDetail.internationalAccountNumber"
            label="Account number *"
          >
            <Input
              name="internationalDetail.internationalAccountNumber"
              placeholder="Enter account number"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="internationalDetail.swiftCode"
            label="Bank swift code *"
          >
            <Input
              name="internationalDetail.swiftCode"
              placeholder="Enter bank swift code"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="internationalDetail.internationalRoutingCode"
            label="International routing code *"
          >
            <Input
              name="internationalDetail.internationalRoutingCode"
              placeholder="Enter international routing code"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24}>
          <FormItem
            name="internationalDetail.internationalConfirmation1"
            className="checkbox-wrapper"
          >
            <Checkbox name="internationalDetail.internationalConfirmation1">
              Please confirm this bank account can accept American Currency
              Transactions
            </Checkbox>
          </FormItem>
        </Col>

        <Col xs={24}>
          <FormItem
            name="internationalDetail.internationalConfirmation2"
            className="checkbox-wrapper"
          >
            <Checkbox name="internationalDetail.internationalConfirmation2">
              I hereby authorize myBasePay, LLC to wire currency entries into
              the account listed above. I understand that it's my responsibility
              to make any changes to my bank account information with myBasePay.
              I understand that there may be additional International Wire
              Transaction Fees which will be disclosed in my Service Agreement.
              I understand myBasePay will not incur the cost of any additional
              fees and will be deducting these amounts from my commission total.
              I will not hold myBasePay liable for any delay in payment
            </Checkbox>
          </FormItem>
        </Col>
      </Row>
    </>
  );
}
